import { ElementType, useCallback, useEffect, useRef, useState } from "react"
import ClickAwayListener from "react-click-away-listener"
import styled, { PolymorphicComponentProps } from "styled-components"

const AbsoluteFloatingBox = styled.dialog`
  position: absolute;
  border: 1px solid ${({ theme }) => theme.colors.grey.$300};
  padding: 0.5rem;
  margin: 0;
  border-radius: 0.5rem;
  background-color: ${({ theme }) => theme.colors.white};
  z-index: 1;
  box-shadow: ${({ theme }) => theme.shadows.mid};

  flex-direction: column;

  display: none;

  &[open] {
    display: flex;
  }

  &:not([open]) {
    display: none;
  }
`

export type FloatingBoxProps<C extends ElementType> = PolymorphicComponentProps<
  "web",
  {
    isOpen: boolean
    anchorElement?: HTMLElement | null
    onClose: () => void
  },
  C,
  C
>

export const DeprecatedFloatingBox = <C extends ElementType = "dialog">({
  children,
  isOpen,
  onClose,
  anchorElement,
  style,
  ...props
}: FloatingBoxProps<C>) => {
  const floatingBoxRef = useRef<HTMLDialogElement>(null)

  const [floatingBoxRect, setFloatingBoxRect] = useState<DOMRect | undefined>(undefined)
  const anchorElementRect: DOMRect | null = anchorElement?.getBoundingClientRect()

  const middleOfAnchorX = anchorElementRect ? anchorElementRect.x + anchorElementRect.width / 2 : 0
  const bottomOfAnchor = anchorElementRect ? anchorElementRect.bottom + 8 : 0
  const floatingBoxWidth = floatingBoxRect?.width ?? 0
  const viewportWidth = window.innerWidth

  const idealX = middleOfAnchorX - floatingBoxWidth / 2
  const idealY = bottomOfAnchor

  // Protect from being too far left or right
  const x = Math.min(Math.max(idealX, 0), viewportWidth - floatingBoxWidth)
  const y = idealY < 0 ? 0 : idealY

  // We can get the width of floatingBox only when open, so we delay the computation of it.
  useEffect(() => {
    if (isOpen) {
      setFloatingBoxRect(floatingBoxRef.current?.getBoundingClientRect())
    }
  }, [isOpen])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose()
      }
    }
    window.addEventListener("keydown", handler)
    return () => {
      window.removeEventListener("keydown", handler)
    }
  }, [onClose])

  return (
    <ClickAwayListener
      onClickAway={(e) => {
        const eventIsFromAnchorEl =
          e instanceof PointerEvent &&
          anchorElementRect instanceof DOMRect &&
          e.x > anchorElementRect.left &&
          e.x < anchorElementRect.right &&
          e.y > anchorElementRect.top &&
          e.y < anchorElementRect.bottom

        if (isOpen && !eventIsFromAnchorEl) {
          onClose()
        }
      }}
    >
      <AbsoluteFloatingBox
        open={isOpen}
        ref={floatingBoxRef}
        {...props}
        style={{
          left: `${x}px`,
          top: `${y}px`,
          ...style,
        }}
      >
        {children}
      </AbsoluteFloatingBox>
    </ClickAwayListener>
  )
}

export const useDeprecatedFloatingBox = (initialIsOpen: boolean = false) => {
  const [isOpen, setIsOpen] = useState(initialIsOpen)
  const anchorElementRef = useRef(null)

  const toogleFloatingBox = useCallback(() => setIsOpen((prev) => !prev), [])
  const closeFloatingBox = useCallback(() => setIsOpen(true), [])
  const openFloatingBox = useCallback(() => setIsOpen(false), [])

  return {
    isOpen,
    openFloatingBox,
    closeFloatingBox,
    toogleFloatingBox,
    anchorElementRef,
  }
}
